/**
 * Global-header trigger for switching identity into a VAU (and back).
 *
 * OPAL-5567 moved this out of the left nav: the switcher now sits in the
 * top bar beside the notification bell / Tracker toggle, injected through
 * the same `slot="system-component"` those buttons use. That slot is the
 * only part of `opti-common-header` Opal renders into — the `?` help icon
 * the ticket positions us against lives *inside* the Stencil component and
 * is not ours to order against, so "left of help" holds only as far as the
 * slot itself sits left of it.
 *
 * Uses the design-system's dedicated `IconSwitchAccount` glyph (added in
 * @optiaxiom/icons 1.6.0, optiaxiom PR #1763).
 *
 * The menu lists the principal's VAUs as avatar + name under a "Switch to"
 * heading, with a "Manage virtual teammates" footer that jumps to the VAU
 * management page — matching the design-team mock (OPAL-5567 follow-up).
 *
 * Still no role byline: the context endpoint returns `{vau_id, vau_name}`
 * only (`vau_context_service._get_available_vaus`) and a VAU instance has no
 * concise role/title field to surface (only `category` — personal/team/public
 * — and a list of `persona.roles_and_responsibilities` sentences, neither of
 * which reads as the "Chief of Staff"-style label in the mock). The avatar is
 * initials derived from the name — the same thing IM does for VAUs in
 * `ConversationAvatar` / `SenderAvatar`, so the two surfaces render the same
 * person the same way without a new API field.
 */

import { IconSwitchAccount, IconUsers } from '@optiaxiom/icons';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Menu,
  MenuContent,
  type MenuOption,
  MenuTrigger,
  Spinner,
  Text,
  Tooltip,
} from '@optiaxiom/react';

import {
  useAssumeVAU,
  useReleaseVAU,
  useVAUContext,
} from '@frontend/features/vau/hooks/useVAUContext';
import { useNamespacedNavigate, useOperations } from '@frontend/hooks';

export const VauSwitchButton = () => {
  // Gated by the `vau_enabled` instance capability.
  const { canInstance } = useOperations();
  const vauFlag = canInstance(['vau_enabled']);

  const navigate = useNamespacedNavigate();
  const { data } = useVAUContext();
  const assume = useAssumeVAU();
  const release = useReleaseVAU();

  if (!vauFlag || !data) return null;

  const isAssumed = data.is_assumed;
  const available = data.available_vaus;

  // Nothing to offer and we're not in VAU mode → don't take a header slot.
  if (!isAssumed && available.length === 0) return null;

  const isSwitching = assume.isPending || release.isPending;
  const switchingMessage = release.isPending
    ? 'Switching back to your profile…'
    : `Switching to ${available.find(v => v.vau_id === assume.variables)?.vau_name ?? 'VAU'}…`;

  const options: MenuOption[] = [];
  if (isAssumed) {
    options.push({
      label: 'Switch back to my profile',
      execute: () => release.mutate({}),
    });
  }
  // Heading over the VAU list — renders as the "SWITCH TO" caption in the mock.
  const switchToGroup = { label: 'Switch to' };
  for (const v of available) {
    options.push({
      label: v.vau_name,
      group: switchToGroup,
      // Profile image when the VAU has one, else initials fallback. `addon`
      // is the Menu's slot for leading content.
      addon: <Avatar name={v.vau_name} src={v.avatar_url || undefined} size="sm" />,
      // The currently assumed VAU is shown but disabled to make the
      // state explicit; clicking it would be a no-op.
      disabledReason:
        isAssumed && data.vau_id === v.vau_id ? 'Already acting as this VAU' : undefined,
      execute: () => assume.mutate(v.vau_id),
    });
  }
  // Footer: jump to the VAU management page. `hidden` group label keeps the
  // divider above it without printing a second caption (mirrors the pattern in
  // DirectoryCta's manage menu).
  options.push({
    label: 'Manage virtual teammates',
    addon: <IconUsers />,
    group: { hidden: true, label: 'Manage', separator: true },
    execute: () => navigate('/vaus'),
  });

  const label = isAssumed ? `Acting as ${data.vau_name ?? 'a VAU'}` : 'Switch identity';

  return (
    <>
      <Menu options={options}>
        <Tooltip content={label} delayDuration={0}>
          <MenuTrigger asChild>
            <Button
              appearance="subtle"
              aria-label={label}
              disabled={isSwitching}
              icon={<IconSwitchAccount />}
              // Matches the bell and Tracker toggle beside it — the ticket
              // calls out that the buttons must be the same size.
              size="lg"
              data-testid="vau-switch-trigger"
            />
          </MenuTrigger>
        </Tooltip>
        <MenuContent />
      </Menu>
      {isSwitching ? (
        // Full-viewport scrim while the identity swap is in flight.
        // Axiom Box exposes a `z` sprinkle (allows `toast`/`tooltip`
        // tokens) but does NOT expose `position`/`top`/`left`/`right`/
        // `bottom` as sprinkles — passing them would be silently dropped
        // and the scrim would render in normal flow inside the header.
        // Same goes for arbitrary `backgroundColor` values. All five
        // layout properties + the dim colour are therefore set via
        // `style` so they actually reach the DOM.
        <Box
          z="toast"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 15, 20, 0.55)',
          }}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            h="full"
            gap="16"
          >
            <Spinner size="xl" />
            <Text fontSize="lg" color="fg.default">
              {switchingMessage}
            </Text>
          </Flex>
        </Box>
      ) : null}
    </>
  );
};
